import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { ColorPicker } from '@actual-app/components/color-picker';
import {
  SvgCheveronDown,
  SvgCheveronUp,
  SvgClose,
  SvgTrash,
} from '@actual-app/components/icons/v1';
import { Input } from '@actual-app/components/input';
import { Tooltip } from '@actual-app/components/tooltip';
import { View } from '@actual-app/components/view';

import {
  useCreateAccountGroupMutation,
  useDeleteAccountGroupMutation,
  useMoveAccountGroupMutation,
  useUpdateAccountGroupMutation,
} from '#accounts';
import { getAccountGroupMoveTarget } from '#accounts/account-groups';
import {
  Modal,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '#components/common/Modal';
import { useAccountGroups } from '#hooks/useAccountGroups';

export function AccountGroupsModal() {
  const { t } = useTranslation();
  const { data: groups = [] } = useAccountGroups();
  const create = useCreateAccountGroupMutation();
  const update = useUpdateAccountGroupMutation();
  const move = useMoveAccountGroupMutation();
  const remove = useDeleteAccountGroupMutation();
  const [newName, setNewName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [draftNames, setDraftNames] = useState<Record<string, string>>({});
  const draftNamesRef = useRef(draftNames);
  draftNamesRef.current = draftNames;
  const editingNameIds = useRef(new Set<string>());
  const [nameErrors, setNameErrors] = useState<Record<string, boolean>>({});

  const active = useMemo(
    () =>
      groups
        .filter(group => !group.tombstone)
        .sort(
          (a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id),
        ),
    [groups],
  );

  useEffect(() => {
    setDraftNames(current => {
      const next = { ...current };
      const activeIds = new Set(active.map(group => group.id));
      for (const group of active) {
        if (
          !(group.id in next) ||
          !editingNameIds.current.has(group.id) ||
          next[group.id] === group.name
        ) {
          next[group.id] = group.name;
        }
      }
      for (const id of Object.keys(next)) {
        if (!activeIds.has(id)) delete next[id];
      }
      return next;
    });
    for (const group of active) {
      if (
        editingNameIds.current.has(group.id) &&
        draftNamesRef.current[group.id] === group.name
      ) {
        editingNameIds.current.delete(group.id);
      }
    }
    for (const id of editingNameIds.current) {
      if (!active.some(group => group.id === id)) {
        editingNameIds.current.delete(id);
      }
    }
  }, [active]);

  return (
    <Modal name="account-groups">
      {({ state: modalState }) => (
        <>
          <ModalHeader
            title={<ModalTitle title={t('Account groups')} />}
            rightContent={
              <ModalCloseButton onPress={() => modalState.close()} />
            }
          />
          <View style={{ padding: 16, overflowY: 'auto' }}>
            {active.length === 0 && (
              <p>
                <Trans>No account groups yet.</Trans>
              </p>
            )}
            {active.map((group, index) => (
              <View
                key={group.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingBottom: 10,
                }}
              >
                <ColorPicker
                  value={group.color ?? undefined}
                  onChange={async color => {
                    try {
                      await update.mutateAsync({
                        id: group.id,
                        fields: { color: color.toString('hex') },
                      });
                    } catch {
                      // The mutation hook owns the notification path.
                    }
                  }}
                >
                  <Button aria-label={t('Choose group color')} variant="bare">
                    <span
                      role="img"
                      aria-label={group.color ?? t('No color')}
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: 7,
                        backgroundColor: group.color ?? '#888',
                      }}
                    />
                  </Button>
                </ColorPicker>
                <Button
                  aria-label={t('Remove group color')}
                  variant="bare"
                  isDisabled={!group.color}
                  onPress={async () => {
                    try {
                      await update.mutateAsync({
                        id: group.id,
                        fields: { color: null },
                      });
                    } catch {
                      // The mutation hook owns the notification path.
                    }
                  }}
                >
                  <SvgClose width={12} height={12} />
                </Button>
                <Input
                  aria-label={t('Group name')}
                  value={draftNames[group.id] ?? group.name}
                  onChangeValue={value => {
                    setDraftNames(current => ({
                      ...current,
                      [group.id]: value,
                    }));
                    editingNameIds.current.add(group.id);
                    setNameErrors(current => ({
                      ...current,
                      [group.id]: false,
                    }));
                  }}
                  onUpdate={async value => {
                    const name = value.trim();
                    if (!name || name === group.name) {
                      editingNameIds.current.delete(group.id);
                      return;
                    }
                    if (name && name !== group.name) {
                      try {
                        await update.mutateAsync({
                          id: group.id,
                          fields: { name },
                        });
                        setDraftNames(current => ({
                          ...current,
                          [group.id]: name,
                        }));
                        editingNameIds.current.delete(group.id);
                        setNameErrors(current => ({
                          ...current,
                          [group.id]: false,
                        }));
                      } catch {
                        setDraftNames(current => ({
                          ...current,
                          [group.id]: group.name,
                        }));
                        editingNameIds.current.delete(group.id);
                        setNameErrors(current => ({
                          ...current,
                          [group.id]: true,
                        }));
                      }
                    }
                  }}
                  style={{ flex: 1 }}
                />
                {nameErrors[group.id] && (
                  <span role="alert">
                    <Trans>Unable to save group name.</Trans>
                  </span>
                )}
                <Tooltip content={t('Move group up')}>
                  <Button
                    aria-label={t('Move group up')}
                    variant="bare"
                    isDisabled={index === 0}
                    onPress={async () => {
                      try {
                        await move.mutateAsync({
                          id: group.id,
                          targetId: getAccountGroupMoveTarget(
                            active,
                            index,
                            'up',
                          ),
                        });
                      } catch {
                        // The mutation hook owns the notification path.
                      }
                    }}
                  >
                    <SvgCheveronUp width={14} height={14} />
                  </Button>
                </Tooltip>
                <Tooltip content={t('Move group down')}>
                  <Button
                    aria-label={t('Move group down')}
                    variant="bare"
                    isDisabled={index === active.length - 1}
                    onPress={async () => {
                      try {
                        await move.mutateAsync({
                          id: group.id,
                          targetId: getAccountGroupMoveTarget(
                            active,
                            index,
                            'down',
                          ),
                        });
                      } catch {
                        // The mutation hook owns the notification path.
                      }
                    }}
                  >
                    <SvgCheveronDown width={14} height={14} />
                  </Button>
                </Tooltip>
                <Tooltip content={t('Delete group')}>
                  <Button
                    aria-label={t('Delete group')}
                    variant="bare"
                    onPress={() => setPendingDelete(group.id)}
                  >
                    <SvgTrash width={14} height={14} />
                  </Button>
                </Tooltip>
              </View>
            ))}
            {pendingDelete && (
              <View style={{ gap: 8, paddingTop: 8 }}>
                {t(
                  'Deleting this group will ungroup its accounts; no accounts or transactions will be deleted.',
                )}
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Button
                    variant="primary"
                    onPress={async () => {
                      try {
                        await remove.mutateAsync({ id: pendingDelete });
                        setPendingDelete(null);
                      } catch {
                        // Keep the confirmation open after a failed delete.
                      }
                    }}
                  >
                    <Trans>Confirm delete</Trans>
                  </Button>
                  <Button onPress={() => setPendingDelete(null)}>
                    <Trans>Cancel</Trans>
                  </Button>
                </View>
              </View>
            )}
            <View style={{ flexDirection: 'row', gap: 8, paddingTop: 8 }}>
              <Input
                aria-label={t('New group name')}
                value={newName}
                onChangeValue={setNewName}
                placeholder={t('New group name')}
              />
              <Button
                variant="primary"
                isDisabled={!newName.trim()}
                onPress={async () => {
                  try {
                    await create.mutateAsync({ name: newName.trim() });
                    setNewName('');
                  } catch {
                    // Keep the entered name after a failed create.
                  }
                }}
              >
                <Trans>Add</Trans>
              </Button>
            </View>
          </View>
        </>
      )}
    </Modal>
  );
}
